/**
 * @description Component-PairInfoCollapse
 */

import { EmptyDataWrap } from '@/components/EmptyDataWrap';
import LeanMoreToolTip from '@/components/ToolTip/LeanMoreToolTip';
import { CHAIN_ID } from '@/constants/chain';
import { PAIR_PAGE_TYPE } from '@/constants/global';
import { LEARN_MORE_LINK } from '@/constants/links';
import { WrappedBigNumber } from '@/entities/WrappedBigNumber';
import { WrappedPair } from '@/entities/WrappedPair';
import { useMarketPairInfo } from '@/features/futures/hooks';
import { useCurrentPairBaseInfoFromUrl } from '@/features/pair/hook';
import { useChainId } from '@/hooks/web3/useChain';

import { useDebounceFn } from 'ahooks';
import classNames from 'classnames';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import ArrowLeft from '../../assets/icon_arrow_left.svg?react';
import FundingRate from '../FundingRate';
import './index.less';
interface IPropTypes {
  children?: React.ReactNode;
  className?: string;
  currentPair?: WrappedPair | undefined;
  type: PAIR_PAGE_TYPE;
}

function PairInfos({ chainId, currentPair }: { chainId: CHAIN_ID | undefined; currentPair: WrappedPair | undefined }) {
  const { t } = useTranslation();
  const currentPairBaseInfo = useCurrentPairBaseInfoFromUrl(chainId);
  const marketPairInfo = useMarketPairInfo(chainId, currentPairBaseInfo?.instrumentAddr, currentPairBaseInfo?.expiry);
  const markPrice = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.markPrice || currentPair?.wrapAttribute('markPrice') || 0);
  }, [currentPair, marketPairInfo?.markPrice]);

  // const effectLiqTvl = useMemo(() => {
  //   return marketPairInfo?.effectLiqTvl || currentPair?.wrapAttribute('effectLiqTvl');
  // }, [currentPair, marketPairInfo?.effectLiqTvl]);

  const openInterestsUsd = useMemo(() => {
    return WrappedBigNumber.from(
      marketPairInfo?.openInterestsUsd || currentPair?.wrapAttribute('openInterestsUSD') || 0,
    );
  }, [currentPair, marketPairInfo?.openInterestsUsd]);

  const longOi = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.longOi || currentPair?.wrapAttribute('longOpenInterest') || 0);
  }, [currentPair, marketPairInfo?.longOi]);

  const shortOi = useMemo(() => {
    return WrappedBigNumber.from(marketPairInfo?.shortOi || currentPair?.wrapAttribute('shortOpenInterest') || 0);
  }, [currentPair, marketPairInfo?.shortOi]);

  const baseToken = useMemo(() => {
    return marketPairInfo?.baseToken || currentPair?.rootInstrument?.baseToken;
  }, [currentPair?.rootInstrument?.baseToken, marketPairInfo?.baseToken]);
  return (
    <>
      <div className="syn-pair-info-right-item">
        <div className="syn-pair-info-right-item-title">
          <LeanMoreToolTip title={t('tooltip.tradePage.markPrice')}>{t('common.markP')}</LeanMoreToolTip>
        </div>
        <div className="syn-pair-info-right-item-value">
          <EmptyDataWrap isLoading={!markPrice || markPrice.eq(0)}>
            {markPrice?.formatPriceNumberWithTooltip({
              isShowTBMK: true,
            })}
          </EmptyDataWrap>
        </div>
      </div>
      {/* 24HV */}
      <div className="syn-pair-info-right-item">
        <div className="syn-pair-info-right-item-title">{t('common.24HV')}</div>
        <div className="syn-pair-info-right-item-value">
          {/* <EmptyDataWrap isLoading={pairStatsStatus !== FETCHING_STATUS.DONE || !currentPair?.stats}>
            {currentPair?.volume24hUSD?.formatNumberWithTooltip({
              isShowTBMK: true,
              prefix: '$',
              isShowApproximatelyEqualTo: false,
            })}
          </EmptyDataWrap> */}

          <EmptyDataWrap isLoading={!marketPairInfo?.volume24hUsd}>
            {WrappedBigNumber.from(marketPairInfo?.volume24hUsd || 0).formatNumberWithTooltip({
              isShowTBMK: true,
              prefix: '$',
              isShowApproximatelyEqualTo: false,
            })}
          </EmptyDataWrap>
        </div>
      </div>

      {/* <div className="syn-pair-info-right-item">
        <div className="syn-pair-info-right-item-title">
          <UnderlineToolTip title={t('common.effectLiqTooltip')}>{t('common.effectLiq')}</UnderlineToolTip>
        </div>
        <div className="syn-pair-info-right-item-value">
          <div className="syn-pair-info-right-item-value-oi">
            <EmptyDataWrap isLoading={!effectLiqTvl}>
              {effectLiqTvl?.formatNumberWithTooltip({
                isShowTBMK: true,
                prefix: '$',
                isShowApproximatelyEqualTo: false,
              })}
            </EmptyDataWrap>
          </div>
        </div>
      </div> */}
      {/* <div className="syn-pair-info-right-item">
        <div className="syn-pair-info-right-item-title">
          {t('common.tvl')}
          <TVLWarningIcon tvl={marketPairInfo?.tvlUsd} />
        </div>
        <div className="syn-pair-info-right-item-value">
          <div className="syn-pair-info-right-item-value-oi">
            <EmptyDataWrap isLoading={!marketPairInfo?.tvlUsd}>
              {marketPairInfo?.tvlUsd?.formatNumberWithTooltip({
                isShowTBMK: true,
                prefix: '$',
                isShowApproximatelyEqualTo: false,
              })}
            </EmptyDataWrap>
          </div>
        </div>
      </div> */}

      {/* estPoolApy */}
      {/* <div className="syn-pair-info-right-item">
        <div className="syn-pair-info-right-item-title">{t('common.earn.estPoolApy')}</div>
        <div className="syn-pair-info-right-item-value">
          <EmptyDataWrap isLoading={!marketPairInfo?.liquidityApy}>
            {marketPairInfo?.liquidityApy?.formatPercentage({
              hundredfold: true,
              colorShader: false,
            })}
          </EmptyDataWrap>
        </div>
      </div> */}

      <FundingRate chainId={chainId} currentPair={currentPair} marketPairInfo={marketPairInfo} />
      {/* Oi long short */}
      {/* openInterest */}
      {(import.meta.env.VITE_AWS_ENV === 'dev' || import.meta.env.VITE_AWS_ENV === 'pre') && (
        <div className="syn-pair-info-right-item">
          <div className="syn-pair-info-right-item-title">
            <LeanMoreToolTip link={LEARN_MORE_LINK.oi} title={t('tooltip.tradePage.openInterest')}>
              {t('common.oi')}
            </LeanMoreToolTip>
          </div>
          <div className="syn-pair-info-right-item-value">
            <EmptyDataWrap isLoading={!openInterestsUsd}>
              {openInterestsUsd?.formatNumberWithTooltip({
                isShowTBMK: true,
                prefix: '$',
              })}
            </EmptyDataWrap>
          </div>
        </div>
      )}
      {import.meta.env.VITE_APP_PORTFOLIO_SHOW_PNL === 'true' && (
        <div className="syn-pair-info-right-item">
          <div className="syn-pair-info-right-item-title">
            {
              <Trans
                i18nKey={'common.longOiShortOi2'}
                components={{
                  color: <span />,
                }}
              />
            }
          </div>
          <div className="syn-pair-info-right-item-value">
            <EmptyDataWrap isLoading={!longOi}>
              <div className="syn-pair-info-right-item-value-oi">
                {longOi?.formatNumberWithTooltip({
                  isShowTBMK: true,
                })}
                <span>{` / `}</span>

                {shortOi?.formatNumberWithTooltip({
                  isShowTBMK: true,
                  suffix: baseToken?.symbol,
                })}
              </div>
            </EmptyDataWrap>
          </div>
        </div>
      )}
    </>
  );
}

const PairInfoCollapse: FC<IPropTypes> = function ({ currentPair }) {
  const chainId = useChainId();
  const [widthDifference, setWidthDifference] = useState(0);
  const [distanceToLeft, setDistanceToLeft] = useState(0);
  const { run: resize } = useDebounceFn(
    () => {
      const infoRightWidth = document.getElementById('infoRight')?.offsetWidth || 0;
      const infoRightContainerWidth = document.getElementById('infoRightContainer')?.offsetWidth || 0;
      setWidthDifference(infoRightContainerWidth - infoRightWidth);
      setDistanceToLeft(0);
    },
    { wait: 200 },
  );
  const moveRight = useCallback(() => {
    const newDistance = distanceToLeft + widthDifference;
    setDistanceToLeft(newDistance);
  }, [distanceToLeft, widthDifference]);
  const moveLeft = useCallback(() => {
    const newDistance = distanceToLeft - widthDifference;
    setDistanceToLeft(newDistance);
  }, [distanceToLeft, widthDifference]);
  useEffect(() => {
    resize();
    setDistanceToLeft(0);
    setWidthDifference(0);
  }, [currentPair?.id]);
  useEffect(() => {
    window.addEventListener('resize', resize);
  }, [resize]);
  const showLeft = useMemo(() => distanceToLeft > 0, [distanceToLeft]);
  const showRight = useMemo(() => distanceToLeft < widthDifference, [distanceToLeft, widthDifference]);

  return (
    <div className="syn-pair-info-right-wrap">
      {widthDifference > 0 && (
        <>
          <div onClick={moveLeft} className={classNames('syn-pair-info-right-arrow left', { show: showLeft })}>
            {' '}
            <ArrowLeft />
          </div>

          <div onClick={moveRight} className={classNames('syn-pair-info-right-arrow right', { show: showRight })}>
            <ArrowLeft />
          </div>
        </>
      )}
      <div
        id="infoRight"
        className={classNames('syn-pair-info-right-container', {
          left: showLeft,
          right: showRight,
        })}>
        <div
          id="infoRightContainer"
          style={{ left: -distanceToLeft }}
          className={classNames('syn-pair-info-right-container-inner')}>
          <PairInfos currentPair={currentPair} chainId={chainId} />
        </div>
      </div>
    </div>
  );
};

export default PairInfoCollapse;
